import { Dispatch, SetStateAction, useEffect, useState } from "react"

// ** Third Party Components
import axios from "axios"
import { parseCookies } from "nookies"
import { useMutation } from "@tanstack/react-query"
import { AlertCircle, CheckCircle2, Clock } from "lucide-react"

// ** UI Components
import { Badge } from "@components/common/badge"
import { Input } from "@components/common/input"
import { Button } from "@components/common/button"
import { Checkbox } from "@components/common/checkbox"
import { ScrollArea } from "@components/common/scroll-area"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@components/common/dialog"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@components/common/table"

// ** Types
import { DictType } from "@pma/types"

// ** Utils
import { cn, getFinancialErrorMessage } from "@pma/utils/helpers"
import { ACCESS_TOKEN } from "@pma/utils/constants"
import {
  APPROVE_INITIAL_PRICE_BOQ_ENDPOINT,
  BOQ_REQUEST_NEW_ITEM_APPROVE,
  FINANCIAL_SERVER_URL,
} from "@pma/utils/api-routes"

// ** Custom Hooks
import { toast } from "@pma/hooks/use-toast"
import useTranslator from "@pma/hooks/use-translator"

interface Props {
  boqId: string
  showApproveDialog: boolean
  setShowApproveDialog: Dispatch<SetStateAction<boolean>>
  setSelectedItems: (rowId: string, isSelected: boolean) => void
  selectedItems: any[]
  boqItems: any[]
  refetchBoQ: CallableFunction
  forApproval?: boolean
}

const ApproveBoqItems = ({
  boqId,
  refetchBoQ,
  showApproveDialog,
  setShowApproveDialog,
  selectedItems,
  boqItems,
  setSelectedItems,
  forApproval,
}: Props) => {
  const { translate, locale } = useTranslator()

  const [amounts, setAmounts] = useState<Record<string, number>>({})
  const [unitPrices, setUnitPrices] = useState<Record<string, number>>({})
  const [approvalAmounts, setApprovalAmounts] = useState<Record<string, number>>({})

  const isHebrew = locale === "he"
  const accessToken = parseCookies()[ACCESS_TOKEN]
  const textAlign = isHebrew ? "text-right" : "text-left"

  // Shared auth headers for API calls
  const authHeaders = {
    headers: {
      Authorization: `Bearer ${accessToken}`,
      "Content-Type": "application/json",
    },
  }

  // Approve initial BoQ item prices
  const approvePriceBoQ = async (priceList: DictType[]) => {
    const response = await axios.post(
      FINANCIAL_SERVER_URL + APPROVE_INITIAL_PRICE_BOQ_ENDPOINT,
      { boq_id: boqId, boq_approve: priceList },
      authHeaders,
    )
    return response.data
  }

  const handlePriceApproveBoQMutation = useMutation({
    mutationKey: ["approve-price-boq"],
    mutationFn: (priceList: DictType[]) => approvePriceBoQ(priceList),
    onSuccess: () => {
      toast({
        title: translate("Success"),
        description: translate("BoQ Approved Successfully"),
        variant: "success",
      })
      setShowApproveDialog(false)
      refetchBoQ()
    },
    onError: (error: any) => {
      toast({
        title: translate("Error"),
        description: getFinancialErrorMessage(error, "approve bill", translate),
        variant: "destructive",
      })
    },
  })

  // Approve new/additional BoQ items with amount & price
  const approveAdditionalAmount = async (payload: any) => {
    const response = await axios.post(
      FINANCIAL_SERVER_URL + BOQ_REQUEST_NEW_ITEM_APPROVE,
      payload,
      authHeaders,
    )
    return response.data
  }

  const handleApproveAdditionalAmountMutation = useMutation({
    mutationKey: ["approve-amount-boq-item"],
    mutationFn: approveAdditionalAmount,
    onSuccess: () => {
      toast({
        title: translate("Success"),
        description: translate("Additional amount approved successfully"),
        variant: "success",
      })
      setShowApproveDialog(false)
      setSelectedItems("", true)
      refetchBoQ()
    },
    onError: (error: DictType) => {
      toast({
        title: translate("Error"),
        description: getFinancialErrorMessage(error, "approve amount", translate),
        variant: "destructive",
      })
    },
  })

  // Find BoQ item by ID from the items list
  const findItem = (itemId: string) =>
    boqItems?.find((item) => parseInt(item?.id) === parseInt(itemId))

  // Build payload and trigger approval mutation
  const confirmApprove = () => {
    if (forApproval) {
      const boqNewItemIds: any[] = []
      const updatedItems = selectedItems.map((itemId) => {
        const item = findItem(itemId)
        boqNewItemIds.push(item?.id)
        return {
          boq_new_item_id: item?.id,
          amount: amounts[item?.id] ?? item?.new_item_data?.amount,
          item_price: unitPrices[item?.id] ?? item?.new_item_data?.cost,
        }
      })
      const payload = { boq_new_item_id: boqNewItemIds, boq_new_item: updatedItems }
      return handleApproveAdditionalAmountMutation.mutate(payload)
    }

    const updatedItems = selectedItems.map((itemId) => {
      const item = findItem(itemId)
      return {
        item_id: item?.id,
        item_price: approvalAmounts[item?.id] || item?.amount,
      }
    })
    return handlePriceApproveBoQMutation.mutate(updatedItems)
  }

  // Render status badge based on approval state
  const getStatusBadge = (item: DictType) => {
    if (item?.approved_amount === 0) {
      return (
        <Badge variant='disabled' className='flex items-center gap-1'>
          <Clock className='h-3 w-3' /> {translate("Pending Approval")}
        </Badge>
      )
    }
    if (
      item?.approved_initial_cost < 1000 ||
      item?._amount_changes?.approved_amount !== item?._amount_changes?.requested_amount
    ) {
      return (
        <Badge variant='default' className='flex items-center gap-1'>
          <AlertCircle className='h-3 w-3' /> {translate("Partially Approved")}
        </Badge>
      )
    }
    return <Badge variant='secondary'>{translate("Approved")}</Badge>
  }

  // Sync approval amounts when selected items change
  useEffect(() => {
    selectedItems.forEach((item: DictType) => {
      const numValue = Number.parseFloat(item?.requested_amount)
      if (!isNaN(numValue) && numValue >= 0) {
        setApprovalAmounts((prev) => ({ ...prev, [item?.id]: numValue }))
      }
    })
  }, [selectedItems])

  const isLoading =
    handleApproveAdditionalAmountMutation.isPending || handlePriceApproveBoQMutation.isPending

  return (
    <Dialog open={showApproveDialog} onOpenChange={setShowApproveDialog}>
      <DialogContent
        id='approve-items'
        className={cn(
          "flex h-full max-h-[95vh] w-full max-w-[95vw] flex-col",
          isHebrew ? "rtl" : "ltr",
        )}
      >
        <DialogHeader>
          <DialogTitle className={cn(textAlign)}>{translate("Approve Items")}</DialogTitle>
          <DialogDescription className={cn(textAlign)}>
            {translate("Are you sure you want to approve the selected")}
            <strong>
              ({selectedItems.length}{" "}
              {selectedItems.length > 1 ? translate("items") : translate("item")})?
            </strong>
          </DialogDescription>
        </DialogHeader>

        <div className='py-4'>
          <ScrollArea className='h-full max-h-[60vh] max-w-[100vw]'>
            <Table className={cn(isHebrew ? "rtl" : "ltr")}>
              <TableHeader>
                <TableRow>
                  <TableHead className='w-[50px]'></TableHead>
                  <TableHead className={cn("w-[100px]", textAlign)}>
                    {translate("Section")}
                  </TableHead>
                  <TableHead className={cn(textAlign)}>{translate("Description")}</TableHead>
                  <TableHead className='w-[20ch] text-right'>{translate("Status")}</TableHead>
                  <TableHead className='w-[80px]'>{translate("Unit")}</TableHead>
                  <TableHead className='w-[80px] text-right'>{translate("Amount")}</TableHead>
                  <TableHead className='w-[120px] text-right'>{translate("Unit Price")}</TableHead>
                  <TableHead className='w-[25ch] text-right'>
                    {translate("approved_amount")}
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {selectedItems?.map((itemId) => {
                  const item = findItem(itemId)
                  const data = item?.new_item_data

                  return (
                    <TableRow
                      key={item?.id}
                      className={cn(
                        item?.status === "error" && "bg-red-50",
                        item?.status === "warning" && "bg-yellow-50",
                      )}
                    >
                      <TableCell>
                        <Checkbox checked={selectedItems.includes(String(item?.id))} />
                      </TableCell>
                      <TableCell className='font-mono text-sm'>{data?.number}</TableCell>
                      <TableCell>
                        <p className='font-medium'>{data?.name}</p>
                      </TableCell>
                      <TableCell>{getStatusBadge(item)}</TableCell>
                      <TableCell>{data?.units}</TableCell>

                      {/* Editable amount field */}
                      <TableCell className='text-right'>
                        {data && (
                          <Input
                            type='number'
                            defaultValue={data?.amount || 0}
                            onChange={(e) =>
                              setAmounts((prev) => ({
                                ...prev,
                                [item?.id]: Number(e.target.value),
                              }))
                            }
                          />
                        )}
                      </TableCell>

                      {/* Editable unit price field */}
                      <TableCell className='text-right'>
                        {data && (
                          <Input
                            type='number'
                            defaultValue={data?.cost || 0}
                            onChange={(e) =>
                              setUnitPrices((prev) => ({
                                ...prev,
                                [item?.id]: Number(e.target.value),
                              }))
                            }
                          />
                        )}
                      </TableCell>

                      {/* Editable approved amount field with overage warning */}
                      <TableCell className='text-right font-medium'>
                        {data && (() => {
                          const contractQty = Number(item?.amount) || 0
                          const paidQty = Number(item?.paid) || 0
                          const remaining = contractQty - paidQty
                          const currentApproval = approvalAmounts[item?.id] ?? data?.amount ?? 0
                          const isOverage = currentApproval > remaining

                          return (
                            <div className='relative'>
                              <Input
                                type='number'
                                defaultValue={data?.amount || 0}
                                className={cn(
                                  isOverage && "border-orange-400 ring-1 ring-orange-300",
                                )}
                                onChange={(e) =>
                                  setApprovalAmounts((prev) => ({
                                    ...prev,
                                    [item?.id]: Number(e.target.value),
                                  }))
                                }
                              />
                              {isOverage && (
                                <Badge
                                  variant='destructive'
                                  className='absolute -top-2 -right-2 text-[9px] px-1 py-0'
                                >
                                  <AlertCircle className='h-3 w-3 mr-0.5' />
                                  {translate("over_request")}
                                </Badge>
                              )}
                            </div>
                          )
                        })()}
                      </TableCell>
                    </TableRow>
                  )
                })}
              </TableBody>
            </Table>
          </ScrollArea>
        </div>

        <DialogFooter className={cn(isHebrew ? "sm:gap-2" : "sm:space-x-2")}>
          <Button variant='outline' size={"sm"} onClick={() => setShowApproveDialog(false)}>
            {translate("Cancel")}
          </Button>
          <Button onClick={confirmApprove} size={"sm"} loading={isLoading}>
            <CheckCircle2 className='h-4 w-4' />
            {translate("Approve Items")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

export default ApproveBoqItems
